import { useEffect, useState } from 'react';
import axios from 'axios';
import { SearchBar } from './SearchBar';
import { ViewToggle } from './ViewToggle';
import { ExternalLink, ArrowLeftCircle, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { deleteFromWooCommerce, restoreToOrders, getWooApiUrl, getWooConsumerKey, getWooConsumerSecret } from '../lib/woocommerce';
import { ConfirmationModal } from './ConfirmationModal';
import { subscribeToProducts } from '../lib/supabase';

const ITEMS_PER_PAGE = 20;

export function WooProducts() {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [confirmRestore, setConfirmRestore] = useState<{
    show: boolean;
    product: any | null;
  }>({
    show: false,
    product: null
  });

  const loadWooProducts = async () => {
    try {
      setLoading(true);
      const wooUrl = getWooApiUrl();
      const consumerKey = getWooConsumerKey();
      const consumerSecret = getWooConsumerSecret();

      if (!wooUrl || !consumerKey || !consumerSecret) {
        toast.error('Configuration WooCommerce manquante. Renseignez-la dans la page Connexion.');
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${wooUrl}/wp-json/wc/v3/products`,
        {
          params: {
            category: 138,
            per_page: ITEMS_PER_PAGE,
            page: page,
            status: 'publish',
            orderby: 'date',
            order: 'desc',
            _fields: 'id,name,description,regular_price,permalink,meta_data'
          },
          auth: {
            username: consumerKey,
            password: consumerSecret
          }
        }
      );
      
      const totalPages = parseInt(response.headers['x-wp-totalpages'] || '1');
      setHasMore(page < totalPages);
      
      if (page === 1) {
        setProducts(response.data);
      } else {
        setProducts(prev => [...prev, ...response.data]);
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des produits WooCommerce:', error);
      toast.error(error.response?.data?.message || error.message || 'Erreur lors du chargement des produits WooCommerce');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWooProducts();
    const unsubscribe = subscribeToProducts(loadWooProducts);
    return () => {
      if (unsubscribe && typeof unsubscribe.unsubscribe === 'function') {
        unsubscribe.unsubscribe();
      }
    };
  }, [page]);

  const loadMore = () => {
    setPage(prev => prev + 1);
  };

  const handleRestore = async (product: any) => {
    setConfirmRestore({
      show: true,
      product
    });
  };

  const confirmRestoreProduct = async () => {
    if (!confirmRestore.product) return;

    try {
      await restoreToOrders(confirmRestore.product);
      await deleteFromWooCommerce(confirmRestore.product.id);
      setProducts(prev => prev.filter(p => p.id !== confirmRestore.product?.id));
      toast.success('Produit restauré avec succès');
      loadWooProducts();
    } catch (error) {
      console.error('Erreur lors de la restauration:', error);
      toast.error('Erreur lors de la restauration du produit');
    }
  };

  const filteredProducts = products.filter(product => {
    const customerNumber = product.meta_data.find((meta: any) => meta.key === 'customer_number')?.value;
    return customerNumber?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {filteredProducts.map(product => (
        <div key={product.id} className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-semibold">{product.name}</h3>
              <p className="text-sm text-gray-500">
                Client #{product.meta_data.find((meta: any) => meta.key === 'customer_number')?.value}
              </p>
            </div>
            <div className="flex space-x-2">
              <a
                href={product.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-900"
                title="Voir sur WooCommerce"
              >
                <ExternalLink className="w-5 h-5" />
              </a>
              <button
                onClick={() => handleRestore(product)}
                className="text-purple-600 hover:text-purple-900"
                title="Restaurer dans la liste des commandes"
              >
                <ArrowLeftCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: product.description }} />
          </div>
          
          <div className="mt-4 pt-4 border-t">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Prix:</span>
              <span className="text-lg font-bold">{product.regular_price}€</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <div className="min-w-[800px]">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Prix
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredProducts.map(product => (
              <tr key={product.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    Client #{product.meta_data.find((meta: any) => meta.key === 'customer_number')?.value}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-500" dangerouslySetInnerHTML={{ __html: product.description }} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {product.regular_price}€
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex justify-end space-x-3">
                    <a
                      href={product.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-900"
                      title="Voir sur WooCommerce"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                    <button
                      onClick={() => handleRestore(product)}
                      className="text-purple-600 hover:text-purple-900"
                      title="Restaurer dans la liste des commandes"
                    >
                      <ArrowLeftCircle className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Produits WooCommerce</h2>
            <div className="flex space-x-4">
              <ViewToggle view={view} onViewChange={setView} />
            </div>
          </div>
          <div className="mt-4">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Recherche par numéro client"
            />
          </div>
        </div>
      </div>

      {loading && page === 1 ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {view === 'grid' ? renderGridView() : renderListView()}

          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={loadMore}
                className="flex items-center px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>
            </div>
          )}
        </>
      )}

      <ConfirmationModal
        isOpen={confirmRestore.show}
        onClose={() => setConfirmRestore({ show: false, product: null })}
        onConfirm={confirmRestoreProduct}
        title="Restaurer le produit"
        message="Voulez-vous vraiment retirer ce produit de WooCommerce et le restaurer dans la liste des commandes ?"
      />
    </div>
  );
}